import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BarChart2, Globe, Shield, TrendingUp, DollarSign, Users, Award, ChevronRight } from 'lucide-react';
import './Home.css';

// Count-up animation component for numeric metrics
function CountUp({ end, duration = 2, prefix = '', suffix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            let start = 0;
            const increment = end / (duration * 60);
            const step = () => {
              start += increment;
              if (start < end) {
                setCount(start);
                requestAnimationFrame(step);
              } else {
                setCount(end);
              }
            };
            requestAnimationFrame(step);
          }
        });
      },
      { threshold: 0.6 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);
  const display = Number.isInteger(end)
    ? count.toFixed(0)
    : count.toFixed(1);
  return (
    <>{prefix}<span ref={ref}>{display}</span>{suffix}</>
  );
}

function Home() {
  return (
    <div className="home-container">
      <section className="hero-section">
        <div className="hero-content">
          <h1>Innovative Investing for the Modern Era</h1>
          <p>SwanPoint: Merging cutting-edge technology with financial expertise.</p>
          <Link to="/portfolio" className="cta-button">Explore Our Strategies <ArrowRight size={20} /></Link>
        </div>
      </section>

      <section className="key-metrics">
        <div className="metric-item">
          <DollarSign size={32} />
          <h3><CountUp end={1.5} duration={2} prefix="$" suffix="M" /></h3>
          <p>Assets Under Management</p>
        </div>
        <div className="metric-item">
          <TrendingUp size={32} />
          <h3><CountUp end={68} duration={2} suffix="%" /></h3>
          <p>2025 Return (Kleos Fund)</p>
        </div>
        <div className="metric-item">
          <Users size={32} />
          <h3><CountUp end={3} duration={2} suffix=" Years" /></h3>
          <p>Active Management</p>
        </div>
        <div className="metric-item">
          <Award size={32} />
          <h3><CountUp end={217} duration={2} suffix="%" /></h3>
          <p>3-Year Cumulative Return (Kleos fund)</p>
        </div>
      </section>

      <section className="features-section">
        <h2>The SwanPoint Advantage</h2>
        <div className="features-grid">
          <div className="feature-item">
            <BarChart2 size={32} />
            <h3>Data-Driven Insights</h3>
            <p>Leveraging advanced analytics and AI for informed decision-making.</p>
          </div>
          <div className="feature-item">
            <Globe size={32} />
            <h3>Global Perspective</h3>
            <p>Diverse strategies across markets and asset classes for optimal diversification.</p>
          </div>
          <div className="feature-item">
            <Shield size={32} />
            <h3>Risk Management</h3>
            <p>Sophisticated models to protect and grow investments in all market conditions.</p>
          </div>
          <div className="feature-item">
            <TrendingUp size={32} />
            <h3>Consistent Performance</h3>
            <p>Proven track record of delivering above-market returns since inception.</p>
          </div>
        </div>
      </section>

      <section className="funds-overview">
        <h2>Our Premier Funds</h2>
        <div className="funds-grid">
          <div className="fund-card">
            <div className="fund-img-placeholder kleos-gradient"></div>
            <h3>Kleos Alpha Fund</h3>
            <p>High-growth quantitative strategy leveraging market inefficiencies.</p>
            <div className="fund-metrics">
              <div className="fund-metric">
                <span>YTD Return</span>
                <strong>68%</strong>
              </div>
              <div className="fund-metric">
                <span>3-Year Return</span>
                <strong>217%</strong>
              </div>
              <div className="fund-metric">
                <span>AUM</span>
                <strong>$410K</strong>
              </div>
            </div>
            <Link to="/portfolio" className="learn-more">Learn More <ChevronRight size={16} /></Link>
          </div>
          <div className="fund-card">
            <div className="fund-img-placeholder prometheus-gradient"></div>
            <h3>Prometheus Balanced Fund</h3>
            <p>Stable growth through strategic asset allocation and risk management.</p>
            <div className="fund-metrics">
              <div className="fund-metric">
                <span>YTD Return</span>
                <strong>33%</strong>
              </div>
              <div className="fund-metric">
                <span>3-Year Return</span>
                <strong>110%</strong>
              </div>
              <div className="fund-metric">
                <span>AUM</span>
                <strong>$1.1M</strong>
              </div>
            </div>
            <Link to="/portfolio" className="learn-more">Learn More <ChevronRight size={16} /></Link>
          </div>
        </div>
      </section>

      <section className="testimonial-section">
        <blockquote>
          "SwanPoint's innovative approach and consistent performance have exceeded my expectations. Their team's expertise and dedication to client success are truly commendable."
        </blockquote>
        <p className="testimonial-author">- Clement, a client</p>
      </section>

      <section className="market-insights">
        <h2>Latest Market Insights</h2>
        <div className="insights-grid">
          <div className="insight-item">
            <h3>Q3 2025 Market Outlook</h3>
            <p>Our analysis of global economic trends and their impact on investment strategies.</p>
            <Link to="/news" className="read-more">Read More <ChevronRight size={16} /></Link>
          </div>
          <div className="insight-item">
            <h3>The Rise of AI in Finance</h3>
            <p>Exploring investment opportunities in artificial intelligence and machine learning.</p>
            <Link to="/news" className="read-more">Read More <ChevronRight size={16} /></Link>
          </div>
        </div>
        <Link to="/news" className="view-all-insights">View All Insights <ArrowRight size={20} /></Link>
      </section>

      <section className="cta-section">
        <h2>Ready to Elevate Your Investment Strategy?</h2>
        <p>Join SwanPoint today and experience the future of investment management.</p>
        <Link to="/contact" className="cta-button">Schedule a Consultation <ArrowRight size={20} /></Link>
      </section>
    </div>
  );
}

export default Home;